import { useNavigate } from "react-router";
import * as motion from "motion/react-client";
import { appearanceAnimation } from "../../../utils/animations";
import { CheckCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button, Card, Typography } from "antd";
import { path } from "../../../utils/common/constant";

interface PackageItem {
  name: string;
  price: string;
  features: string[];
  description: string;
  typeVisa: string;
}

const CategoriesSection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const packagesData: PackageItem[] = [
    {
      name: t("home.categories.name1"),
      price: t("home.categories.price1"),
      features: [
        t("home.categories.features1"),
        t("home.categories.features2"),
        t("home.categories.features3"),
      ],
      description: t("home.categories.description1"),
      typeVisa: "OneMonthSingle",
    },
    {
      name: t("home.categories.name2"),
      price: t("home.categories.price2"),
      features: [
        t("home.categories.features4"),
        t("home.categories.features5"),
        t("home.categories.features6"),
      ],
      description: t("home.categories.description2"),
      typeVisa: "OneMonthMultiple",
    },
    {
      name: t("home.categories.name3"),
      price: t("home.categories.price3"),
      features: [
        t("home.categories.features7"),
        t("home.categories.features8"),
        t("home.categories.features9"),
      ],
      description: t("home.categories.description3"),
      typeVisa: "ThreeMonthSingle",
    },
    {
      name: t("home.categories.name4"),
      price: t("home.categories.price4"),
      features: [
        t("home.categories.features10"),
        t("home.categories.features11"),
        t("home.categories.features12"),
      ],
      description: t("home.categories.description4"),
      typeVisa: "ThreeMonthMultiple",
    },
  ];
  const handleClickPackage = (typeVisa: string) => {
    navigate(path.VISA_APPLICATION, {
      state: {
        typeVisa,
        processingTime: "Standard",
      },
    });
  };
  return (
    <div className="w-full flex justify-center items-center px-6 py-20">
      <div className="w-full max-w-[1290px] flex flex-col gap-28 justify-center items-center">
        {/* Title */}
        <motion.div
          {...appearanceAnimation("bottom")}
          className="flex flex-col justify-center items-center gap-6"
        >
          <p className="text-[#074799] text-sm font-semibold">
            {t("home.categories")}
          </p>
          <p className="text-[#074799] md:text-5xl text-3xl font-bold">
            {t("home.categories.title")}
          </p>
        </motion.div>

        <div className="w-full grid lg:grid-cols-4 md:grid-cols-2 gap-4">
          {packagesData.map((packageInfo, index) => (
            <PackageCard
              packageInfo={packageInfo}
              key={index}
              handleClickPackage={handleClickPackage}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default CategoriesSection;

interface PackageCardProps {
  packageInfo: PackageItem;
  handleClickPackage: (typeVisa: string) => void;
}
const PackageCard: React.FC<PackageCardProps> = ({
  packageInfo,
  handleClickPackage,
}) => {
  const { t } = useTranslation();
  const { Title, Text } = Typography;
  return (
    <Card className="hover:shadow-lg transition-shadow flex flex-col justify-between">
      <Title level={4} className="text-center mb-4 font-bold">
        {packageInfo.name}
      </Title>

      <div className="flex justify-center mb-6">
        <div className="text-center">
          <Text className="text-4xl font-bold text-primary-blue">
            ${packageInfo.price}
          </Text>
          <Text className="text-gray-500">/Pax</Text>
        </div>
      </div>

      <div className="space-y-4 mb-6">
        {packageInfo.features.map((feature, index) => (
          <div className="flex items-center gap-2" key={index}>
            <CheckCircle className="text-green-500" size={20} />
            <Text>{feature}</Text>
          </div>
        ))}
      </div>

      <Text
        className="text-gray-600 mb-4 block text-sm text-justify line-clamp-6"
        style={{
          height: "calc(1.25rem * 6)",
        }}
      >
        {packageInfo.description}
      </Text>

      <Button
        type="primary"
        block
        size="large"
        className="bg-primary-lightyellow hover:bg-primary-yellow text-primary-blue border-none rounded-full font-semibold"
        onClick={() => handleClickPackage(packageInfo.typeVisa)}
      >
        {t("emergency_visa.side_bar.apply_now")}
      </Button>
    </Card>
  );
};
